import {
  HardHat,
  Search,
  Layers,
  MountainSnow,
  Droplets,
} from "lucide-react";

/**
 * Données des 5 expertises ETANCIA.
 * Pour ajouter/modifier un chantier de référence, éditez simplement le tableau `references`
 * de l'expertise concernée ci-dessous. Chaque chantier a :
 *   - title : nom du chantier
 *   - location : ville / département
 *   - year : année
 *   - description : quelques mots sur le chantier
 *   - image : URL principale (peut être vide "")
 *   - images : (optionnel) tableau d'URLs supplémentaires pour la galerie
 */
export const SERVICES = [
  {
    slug: "gros-travaux",
    icon: HardHat,
    title: "Gros travaux",
    shortDesc:
      "Rénovation complète et réfection d'étanchéité sur toiture-terrasse, dalles et structures bâtiment.",
    longDesc:
      "Nous prenons en charge la réfection complète de vos toitures-terrasses : dépose de l'ancien complexe, préparation du support, pose du pare-vapeur, isolation, membrane d'étanchéité (bitume, PVC ou EPDM) et finitions. Chaque chantier bénéficie de la garantie décennale et d'un suivi rigoureux, de l'étude jusqu'à la réception.",
    features: [
      "Réfection totale d'étanchéité",
      "Dépose et évacuation de l'ancien complexe",
      "Pose d'isolation thermique (PIR, PSE, laine de roche)",
      "Membranes bitume, PVC, EPDM",
      "Relevés, jonctions et évacuations pluviales",
      "Garantie décennale documentée",
    ],
    references: [
      {
        title: "Rechapage entrepôt industriel 5000 m²",
        location: "Senonches 28250",
        year: "2026",
        description:
          "Rechapage complet d'une toiture-terrasse inaccessible sur un entrepôt industriel de 5000 m². Dépose partielle, préparation du support, pose d'une nouvelle membrane bitume élastomère bicouche avec traitement complet des relevés et évacuations pluviales.",
        image: "/assets/chantiers/senonches-1.jpeg",
        images: [
          "/assets/chantiers/senonches-1.jpeg",
          "/assets/chantiers/senonches-2.jpeg",
          "/assets/chantiers/senonches-3.jpeg",
        ],
      },
      {
        title: "Réfection complète toiture-terrasse inaccessible 800 m²",
        location: "Boulogne-Billancourt 92100",
        year: "2026",
        description:
          "Réfection complète d'une toiture-terrasse inaccessible de 800 m² sur immeuble résidentiel. Reprise intégrale de l'étanchéité bitumineuse avec installation de plots de sécurité, traitement des souches et remise en conformité des garde-corps.",
        image: "/assets/chantiers/boulogne-1.jpeg",
        images: [
          "/assets/chantiers/boulogne-1.jpeg",
          "/assets/chantiers/boulogne-2.jpeg",
          "/assets/chantiers/boulogne-3.jpeg",
        ],
      },
    ],
  },
  {
    slug: "recherche-fuites",
    icon: Search,
    title: "Recherche de fuites",
    shortDesc:
      "Diagnostic précis par fumigène, colorant ou thermographie pour localiser l'origine exacte d'une infiltration.",
    longDesc:
      "Trouver la source d'une infiltration demande méthode et outils spécifiques. Nos techniciens combinent inspection visuelle, test au fumigène, colorants traceurs et caméra thermique pour localiser l'origine exacte d'une fuite — sans démontage inutile. Rapport détaillé et devis de réparation fournis en fin de mission.",
    features: [
      "Test au fumigène",
      "Colorants traceurs",
      "Caméra thermique / thermographie",
      "Inspection endoscopique",
      "Rapport photo daté",
      "Devis de réparation immédiat",
    ],
    references: [],
  },
  {
    slug: "intervention-ponctuelle",
    icon: Layers,
    title: "Intervention ponctuelle",
    shortDesc:
      "Audit technique, reprise localisée et traitement ciblé sans démontage lourd.",
    longDesc:
      "Une fissure, un joint fatigué, un relevé décollé ? Pas besoin de refaire toute la toiture. Nos équipes interviennent de manière ciblée pour prolonger la vie de votre étanchéité existante, avec des solutions techniques adaptées : reprise à froid, pontage, soudure à chaud, résine liquide.",
    features: [
      "Reprise de joints et fissures",
      "Pontage de fissures actives",
      "Reprise de relevés",
      "Soudure à chaud (bitume, PVC)",
      "Traitement d'urgence",
      "Devis avant intervention",
    ],
    references: [],
  },
  {
    slug: "entretien-toitures",
    icon: MountainSnow,
    title: "Entretien toitures accessibles & inaccessibles",
    shortDesc:
      "Contrats d'entretien périodiques : nettoyage, vérification des relevés, étanchéité préventive.",
    longDesc:
      "L'entretien régulier d'une toiture-terrasse multiplie sa durée de vie par 2. Nous proposons des contrats annuels ou bi-annuels adaptés : nettoyage des évacuations pluviales, vérification de l'ensemble des points singuliers, rapport photo, et petites reprises préventives incluses. Pour toitures accessibles ET inaccessibles (cordistes agréés).",
    features: [
      "Nettoyage EP et gouttières",
      "Contrôle visuel complet",
      "Vérification des relevés et jonctions",
      "Rapport photo daté",
      "Petites reprises préventives",
      "Interventions cordistes agréés",
    ],
    references: [],
  },
  {
    slug: "resines-revetements",
    icon: Droplets,
    title: "Résines & revêtements techniques",
    shortDesc:
      "Travaux de résine, étanchéité liquide, peinture réfléchissante anti-chaleur et revêtements de sol.",
    longDesc:
      "Les résines polyuréthane et méthacrylate offrent une étanchéité liquide sans joint, idéale pour les formes complexes, balcons, terrasses et locaux techniques. Nous appliquons également des peintures réfléchissantes (cool roof) qui réduisent la température de vos toitures de 15 à 20 °C, et posons des revêtements de sol résistants pour parkings, coursives et zones techniques.",
    features: [
      "Résine polyuréthane / méthacrylate",
      "Système d'étanchéité liquide (SEL)",
      "Peinture réfléchissante cool roof",
      "Revêtements de sol PMMA / PU",
      "Formes complexes et détails",
      "Résistance UV et mécanique",
    ],
    references: [
      {
        title: "Résine antidérapante 600 m² — Marché municipal",
        location: "Ris-Orangis 91130",
        year: "2026",
        description:
          "Application d'une résine antidérapante sur 600 m² pour le marché municipal de la ville de Ris-Orangis. Revêtement de sol technique résistant aux passages intensifs, aux produits alimentaires et aux nettoyages haute pression — avec marquage jaune de sécurité pour les zones de circulation.",
        image: "/assets/chantiers/marche-ris-1.jpeg",
        images: [
          "/assets/chantiers/marche-ris-1.jpeg",
          "/assets/chantiers/marche-ris-2.jpeg",
          "/assets/chantiers/marche-ris-3.jpeg",
        ],
      },
      {
        title: "Tapis de marbre — Balcon résidentiel",
        location: "Ris-Orangis 91130",
        year: "2026",
        description:
          "Réalisation d'un tapis de marbre décoratif sur balcon de maison. Mélange de granulats de marbre lié à une résine polyuréthane, offrant une finition esthétique, drainante et durable. Solution idéale pour rénover balcons et terrasses sans surcharge.",
        image: "/assets/chantiers/marbre-ris-1.jpeg",
        images: [
          "/assets/chantiers/marbre-ris-1.jpeg",
          "/assets/chantiers/marbre-ris-2.jpeg",
          "/assets/chantiers/marbre-ris-3.jpeg",
        ],
      },
    ],
  },
];

export function getServiceBySlug(slug) {
  return SERVICES.find((s) => s.slug === slug);
}
